import { CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { leadApi } from "api/lead.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  StatusCell,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useLocation } from "react-router-dom";
import { ActivityLogLocationState } from "types/activityLog";
import { QueryParam } from "types/query";
import { Lead, LeadCounts, LeadSource, LeadStatus } from "types/lead";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { StatusTabs } from "views/Opportunity/components/StatusTabs";
import { LeadModal } from "./components/LeadModal";
import { LEAD_PATH, emptyLeadCounts, leadSourceLabel, leadSourceOptions, leadStatusTrans, leadTabs } from "./lead.meta";
import { LeadFromLinks } from "./components/LeadFromLinks";
import "./Lead.scss";

const countOf = (counts: LeadCounts, key: string) => {
  if (key === "ALL") return counts.all;
  return counts.statuses[key as LeadStatus] || 0;
};

export const LeadPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ALL");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<LeadCounts>(emptyLeadCounts());
  const leadModalRef = useRef<LeadModal>(null);
  const openedFromLogRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    if (openedFromLogRef.current) {
      return;
    }
    const lead = (location.state as ActivityLogLocationState | null)?.lead;
    if (!lead) {
      return;
    }
    openedFromLogRef.current = true;
    leadModalRef.current?.handleUpdate(lead);
  }, [location.state]);

  const fetchData = useCallback(async (queryParams: QueryParam, tabKey: string) => {
    setLoading(true);
    try {
      const params: QueryParam = { ...queryParams };
      if (tabKey !== "ALL") {
        params.status = tabKey;
      }
      const res = await leadApi.findAll(params);
      setData(res.data.leads || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || emptyLeadCounts());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query, tab);
  }, [fetchData, query, tab]);

  const handleDelete = useCallback(
    async (lead: Lead) => {
      setLoading(true);
      try {
        await leadApi.delete(lead.id);
        message.success("Xóa lead thành công");
        await fetchData(query, tab);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query, tab],
  );

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const stopRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const openView = useCallback((lead: Lead) => {
    leadModalRef.current?.handleView(lead);
  }, []);

  const openEdit = useCallback((lead: Lead) => {
    leadModalRef.current?.handleUpdate(lead);
  }, []);

  const openCreate = useCallback(() => {
    leadModalRef.current?.handleCreate();
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handleTabChange = useCallback((key: string) => {
    setTab(key);
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleSourceChange = useCallback((value: string | number | undefined) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      source: value ? String(value) : undefined,
    }));
  }, []);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const refresh = useCallback(() => {
    fetchData(query, tab);
  }, [fetchData, query, tab]);

  const canCreate = can(LEAD_PATH, "CREATE");
  const canEdit = can(LEAD_PATH, "UPDATE");
  const canDelete = can(LEAD_PATH, "DELETE");

  const sourceFilters = useMemo(
    () => [
      {
        key: "source",
        value: (query.source as string | undefined) ?? "",
        options: leadSourceOptions,
        onChange: handleSourceChange,
        placeholder: "Nguồn",
        allowClear: true,
        width: 240,
      },
    ],
    [handleSourceChange, query.source],
  );

  const tabs = leadTabs.map((item) => ({
    ...item,
    count: countOf(counts, item.key),
  }));

  const columns = useMemo<TableColumnsType<Lead>>(
    () => [
      {
        title: "Tên",
        dataIndex: "name",
        key: "name",
        width: 160,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Số điện thoại",
        dataIndex: "phone",
        key: "phone",
        width: 140,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Zalo",
        dataIndex: "zalo",
        key: "zalo",
        width: 140,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Lĩnh vực",
        dataIndex: "category",
        key: "category",
        width: 140,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Ngân sách",
        dataIndex: "estimatedBudget",
        key: "estimatedBudget",
        width: 160,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (value: LeadStatus) => <StatusCell value={value} trans={leadStatusTrans} />,
      },
      {
        title: "Nguồn",
        dataIndex: "source",
        key: "source",
        width: 220,
        render: (value: LeadSource) => <TableContent>{leadSourceLabel[value] || "--"}</TableContent>,
      },
      {
        title: "Đường dẫn",
        dataIndex: "fromLink",
        key: "fromLink",
        width: 260,
        render: (value) => <LeadFromLinks fromLink={value} />,
      },
      {
        title: "Mô tả",
        dataIndex: "description",
        key: "description",
        width: 240,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      ...(canEdit || canDelete
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 120,
            align: "center" as const,
            fixed: "right" as const,
            render: (_: unknown, record: Lead) => (
              <div className="flex gap-2 justify-center items-center" onClick={stopRowClick}>
                {canEdit && (
                  <Tooltip title="Cập nhật">
                    <ActionButton aria-label="Cập nhật" onClick={() => openEdit(record)}>
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa?"
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDelete, canEdit, handleDelete, openEdit, stopRowClick],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div className="leadPage">
      <StatusTabs items={tabs} activeKey={tab} onChange={handleTabChange} />
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 360,
              placeholder: "Tìm theo tên, SĐT, zalo, lĩnh vực, mô tả",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              canCreate && (
                <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Tạo mới
                </AppButton>
              )
            }
            filterLabel={null}
            filters={sourceFilters}
          />
        }
        table={
          <AppTable<Lead>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            onRow={(record) => ({
              onClick: () => openView(record),
            })}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <LeadModal ref={leadModalRef} onSubmitOk={refresh} onClose={() => { }} />
    </div>
  );
});
